import { createContext, useContext, useState, useMemo, useEffect } from "react";
import { getAuthLocal, setAuthLocal, removeAuthLocal } from "../core/AuthHelper";
import { verifyTokenApi } from "../layouts/authentication/_request";

// Material Dashboard 2 React main context
const AuthContext = createContext();

import PropTypes from "prop-types";

// Setting custom name for the context which is visible on react dev tools
AuthContext.displayName = "AuthContext";

const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(getAuthLocal());
  const [verifiedCustomer, setVerifiedCustomer] = useState(undefined);
  const [miniSidenav, setMiniSidenav] = useState(false);
  const [transparentSidenav, setTransparentSidenav] = useState(false);
  const [whiteSidenav, setWhiteSidenav] = useState(false);
  const [sidenavColor, setSidenavColor] = useState("info");
  const [transparentNavbar, setTransparentNavbar] = useState(false);
  const [fixedNavbar, setFixedNavbar] = useState(false);
  const [openConfigurator, setOpenConfigurator] = useState(false);
  const [direction, setDirection] = useState("ltr");
  const [layout, setLayout] = useState("dashboard");
  const [darkMode, setDarkMode] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);

  const [snackbarMessage, setSnackbarMessage] = useState({
    type: "",
    message: "",
  });

  const saveAuthData = async (auth) => {
    setAuth(auth);
    if (auth) {
      setAuthLocal(auth);
      const customer = await verifyTokenFunc(auth);
      return customer;
    } else {
      removeAuthLocal();
      setVerifiedCustomer(undefined);
    }
  };

  const logout = () => {
    saveAuthData(undefined);
    localStorage.clear();
  };

  const showSnackbarWithMessage = (message, displayTime) => {
    setSnackbarMessage(message);
    setShowSnackbar(true);
    setTimeout(
      () => {
        hideSnackbar();
      },
      displayTime ? displayTime : 2000
    );
  };

  const hideSnackbar = () => {
    setShowSnackbar(false);
    setSnackbarMessage({
      type: "",
      message: "",
    });
  };

  useEffect(() => {
    const storedDarkMode = localStorage.getItem("darkMode");
    if (storedDarkMode !== null) {
      setDarkMode(JSON.parse(storedDarkMode));
    }
  }, [localStorage.getItem("darkMode")]);

  const verifyTokenFunc = async (auth) => {
    try {
      const {
        data: { success, data },
      } = await verifyTokenApi({ accessToken: auth.accessToken });
      if (success) {
        const verified = { ...data.verifiedData };
        setVerifiedCustomer(verified);
        return verified;
      } else {
        logout();
      }
    } catch (e) {
      console.log(e.message);
    }
  };

  const value = useMemo(
    () => ({
      auth,
      setAuth,
      miniSidenav,
      setMiniSidenav,
      transparentSidenav,
      setTransparentSidenav,
      whiteSidenav,
      setWhiteSidenav,
      sidenavColor,
      setSidenavColor,
      transparentNavbar,
      setTransparentNavbar,
      fixedNavbar,
      setFixedNavbar,
      openConfigurator,
      setOpenConfigurator,
      direction,
      setDirection,
      layout,
      setLayout,
      darkMode,
      setDarkMode,
      saveAuthData,
      logout,
      showSnackbar,
      setShowSnackbar,
      snackbarMessage,
      setSnackbarMessage,
      showSnackbarWithMessage,
      hideSnackbar,
      verifiedCustomer,
      setVerifiedCustomer,
      verifyTokenFunc,
    }),
    [
      auth,
      setAuth,
      miniSidenav,
      transparentSidenav,
      whiteSidenav,
      sidenavColor,
      transparentNavbar,
      fixedNavbar,
      openConfigurator,
      direction,
      layout,
      darkMode,
      saveAuthData,
      logout,
      verifiedCustomer,
      verifyTokenFunc,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth should be used inside the AuthProvider.");
  }

  return context;
};

export { AuthProvider, useAuth };
